import {
  AiCategory,
  Band,
  BlobMediaKind,
  DecisionStatus,
  DecisionType,
  DisagreementReason,
  PAGE_SUBJECT_KINDS,
  BLOB_SUBJECT_KINDS,
  SubjectKind,
  TriggerReason,
} from './enums';

export interface QueueListItemDecision {
  id: number;
  contentModerationEventId: number;
  decision: DecisionType;
  agreedWithAi: boolean;
  isAutomated: boolean;
  decidedAt: string;
  reviewerAccountId: number;
  disagreementReason: DisagreementReason | null;
  notes: string | null;
  status: DecisionStatus;
  supersededAt: string | null;
  supersededByAccountId: number | null;
  supersededByDecisionId: number | null;
  supersededReason: string | null;
}

export interface QueueListItem {
  contentModerationEventId: number;
  subjectKind: SubjectKind;
  fundraiserId: number | null;
  teamId: number | null;
  collectionId: number | null;
  blobPath: string | null;
  blobMediaKind: BlobMediaKind | null;
  linkedAccountId: number | null;
  linkedOrganizationId: number | null;
  triggerReason: TriggerReason;
  triggeredAt: string;
  band: Band;
  aiScore: number;
  aiCategory: AiCategory;
  aiConfidence: number;
  autoDecidedAt: string | null;
  lockedByReviewerAccountId: number | null;
  lockLastHeartbeatAt: string | null;
  pageNameSnapshot: string | null;
  activeDecision: QueueListItemDecision | null;
}

export type QueueSortKey = 'scoreDesc' | 'ageDesc' | 'ageAsc';

export type QueueSubjectFilter = 'pages' | 'blobs';

// Backend filter for the active-decision state of an event. Undecided = no
// Active reviewer decision row (the work-pile). Decided = Active reviewer
// decision row exists. null = no filter (audit/admin views).
export type QueueDecisionState = 'undecided' | 'decided' | null;

export interface QueueFilters {
  band: Band;
  subjectFilter: QueueSubjectFilter;
  minScore: number;
  maxScore: number;
  decisionState: QueueDecisionState;
  linkedAccountId: number | null;
  linkedOrganizationId: number | null;
  // Subject-scoped filters. When any of these is set the queue switches to
  // entity-history mode (see helpers/entityScope) — the work-pile filters
  // above are ignored and we show the full moderation history for that entity.
  fundraiserId: number | null;
  teamId: number | null;
  collectionId: number | null;
  sort: QueueSortKey;
  // 1-based page number on the wire (the API migrated off skip/take to
  // page/pageSize). Always send both — the response echoes them back and the
  // UI uses totalCount + page + pageSize to render "Showing X of N".
  page: number;
  pageSize: number;
}

// Paged-response envelope for the queue endpoint. data.list carries the rows;
// totalCount/page/pageSize drive the count display (and eventually paginator
// controls). Generic on the row type so other paged endpoints can reuse it.
export interface QueuePage<T> {
  list: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export const DEFAULT_QUEUE_FILTERS: QueueFilters = {
  band: 'humanReview',
  subjectFilter: 'pages',
  // Default to All scores (1–10). The score dropdown also offers 1–3 / 4–7 /
  // 8–10 buckets the reviewer can switch to.
  minScore: 1,
  maxScore: 10,
  decisionState: 'undecided',
  linkedAccountId: null,
  linkedOrganizationId: null,
  fundraiserId: null,
  teamId: null,
  collectionId: null,
  sort: 'scoreDesc',
  page: 1,
  pageSize: 50,
};

export interface EntityScope {
  kind: 'fundraiser' | 'team' | 'collection';
  id: number;
}

// Detect entity-history scope from the filters. Exactly one of the subject ids
// is expected when scoped; we check in a fixed precedence order.
export function entityScopeFromFilters(filters: QueueFilters): EntityScope | null {
  if (filters.fundraiserId != null) return { kind: 'fundraiser', id: filters.fundraiserId };
  if (filters.teamId != null) return { kind: 'team', id: filters.teamId };
  if (filters.collectionId != null) return { kind: 'collection', id: filters.collectionId };
  return null;
}

export function subjectKindsForFilter(filter: QueueSubjectFilter): readonly SubjectKind[] {
  return filter === 'pages' ? PAGE_SUBJECT_KINDS : BLOB_SUBJECT_KINDS;
}

// Backend expects PascalCase for the decisionState enum, even though the
// rest of our enums are camelCase. The backend doc spells it as "Decided" /
// "Undecided", so flip the case at the wire boundary only.
function decisionStateWireValue(value: Exclude<QueueDecisionState, null>): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function toUrlSearchParams(filters: QueueFilters): URLSearchParams {
  const params = new URLSearchParams();
  const scope = entityScopeFromFilters(filters);

  if (scope) {
    // Entity-history mode: the entity id is the ONLY filter we send. The
    // work-pile filters (band / decisionState / score / subjectKinds) are
    // deliberately omitted so we get the full moderation history.
    params.set(`${scope.kind}Id`, String(scope.id));
  } else {
    params.set('band', filters.band);
    for (const kind of subjectKindsForFilter(filters.subjectFilter)) {
      params.append('subjectKinds', kind);
    }
    params.set('minScore', String(filters.minScore));
    params.set('maxScore', String(filters.maxScore));
    if (filters.decisionState !== null) {
      params.set('decisionState', decisionStateWireValue(filters.decisionState));
    }
    if (filters.linkedAccountId !== null) {
      params.set('linkedAccountId', String(filters.linkedAccountId));
    }
    if (filters.linkedOrganizationId !== null) {
      params.set('linkedOrganizationId', String(filters.linkedOrganizationId));
    }
  }

  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  return params;
}
